import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Heart, Luggage, Check, ShieldCheck, LogOut, LogIn, CalendarDays, Loader2 } from "lucide-react";
import { StayHeader } from "@/components/stays/StayHeader";
import { MobileBottomNav } from "@/components/stays/MobileBottomNav";
import Footer from "@/components/Footer";
import { getProfile, saveProfile } from "@/lib/profile";
import { useWishlistCount } from "@/lib/wishlist";
import { setMeta, SITE_URL } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { apiMyBookings, apiUpdateMe } from "@/lib/userAuth";
import { fmtINR } from "@/lib/stayflowApi";

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-ink">{label}</span>
    <input
      name={name} value={value} onChange={onChange} type={type} placeholder={placeholder} data-testid={testid}
      className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
    />
  </label>
);

const Profile = () => {
  const wishCount = useWishlistCount();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "" });
  const [bookings, setBookings] = useState(null);

  useEffect(() => { setMeta({ title: "Your Profile — StayFlow", description: "Save your details for faster booking on StayFlow.", url: `${SITE_URL}/profile` }); }, []);

  useEffect(() => {
    const local = getProfile();
    setForm({
      guest_name: user?.name || local.guest_name || "",
      guest_email: user?.email || local.guest_email || "",
      guest_phone: user?.phone || local.guest_phone || "",
    });
  }, [user]);

  useEffect(() => {
    if (!user) { setBookings(null); return; }
    let active = true;
    apiMyBookings().then((b) => { if (active) setBookings(b); }).catch(() => { if (active) setBookings([]); });
    return () => { active = false; };
  }, [user]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = async () => {
    saveProfile(form);
    if (user) {
      try {
        await apiUpdateMe({ name: form.guest_name, email: form.guest_email, phone: form.guest_phone });
        toast.success("Profile saved to your account");
      } catch { toast.success("Saved on this device"); }
    } else {
      toast.success("Details saved on this device");
    }
  };

  const onSignOut = async () => { await logout(); toast.success("Signed out"); };

  return (
    <div className="bg-paper min-h-screen pb-24 md:pb-0" data-testid="profile-page">
      <StayHeader className="hidden md:block" />
      <main className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex items-center gap-2 text-brand">
          <User className="w-4 h-4" aria-hidden="true" />
          <p className="overline">Your profile</p>
        </div>

        {/* Account state */}
        {user ? (
          <div className="mt-3 rounded-3xl border border-black/8 bg-white p-6 md:p-7 flex items-center gap-4" data-testid="account-card">
            {user.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-14 h-14 rounded-full object-cover ring-1 ring-black/10" />
            ) : (
              <span className="w-14 h-14 rounded-full bg-brand text-white text-xl font-bold flex items-center justify-center">
                {(user.name || user.email || user.phone || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-heading font-extrabold text-xl text-ink truncate" data-testid="account-name">{user.name || "StayFlow guest"}</p>
              <p className="text-sm text-ink-soft truncate" data-testid="account-contact">{user.email || user.phone}</p>
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-brand"><ShieldCheck className="w-3.5 h-3.5" /> Signed in</p>
            </div>
            <button onClick={onSignOut} data-testid="account-signout" className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-black/10 text-ink-soft text-sm px-4 py-2 hover:bg-black/5 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        ) : (
          <div className="mt-3 rounded-3xl border border-black/8 bg-white p-6 md:p-7 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="signin-cta">
            <div className="flex-1">
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight text-ink">Sign in to StayFlow</h1>
              <p className="mt-1.5 text-ink-soft text-sm">Save your wishlist across devices, book faster, and track every stay.</p>
            </div>
            <button onClick={() => navigate("/login", { state: { from: "/profile" } })} data-testid="profile-signin-btn" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-6 h-12 transition-transform hover:-translate-y-0.5">
              <LogIn className="w-4 h-4" /> Sign in
            </button>
          </div>
        )}

        {/* Quick links */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Link to="/wishlist" data-testid="profile-wishlist-link" className="group rounded-2xl border border-black/8 bg-white p-5 flex items-center gap-4 hover:border-brand transition-colors">
            <span className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center"><Heart className="w-5 h-5" /></span>
            <span>
              <span className="block font-heading font-bold text-ink">Wishlist</span>
              <span className="block text-sm text-ink-soft">{wishCount} saved stay{wishCount !== 1 ? "s" : ""}</span>
            </span>
          </Link>
          <Link to="/booking/lookup" data-testid="profile-bookings-link" className="group rounded-2xl border border-black/8 bg-white p-5 flex items-center gap-4 hover:border-brand transition-colors">
            <span className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center"><Luggage className="w-5 h-5" /></span>
            <span>
              <span className="block font-heading font-bold text-ink">Find my booking</span>
              <span className="block text-sm text-ink-soft">Look up by phone or booking ID</span>
            </span>
          </Link>
        </div>

        {/* My bookings (logged-in) */}
        {user && (
          <div className="mt-6 rounded-3xl border border-black/8 bg-white p-6 md:p-8" data-testid="my-bookings">
            <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><CalendarDays className="w-5 h-5 text-brand" /> My bookings</h2>
            {bookings === null ? (
              <div className="mt-4 flex items-center gap-2 text-ink-soft text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : bookings.length === 0 ? (
              <p className="mt-3 text-sm text-ink-soft">No bookings yet. When you book a stay while signed in, it'll show up here.</p>
            ) : (
              <ul className="mt-4 space-y-3" data-testid="my-bookings-list">
                {bookings.map((b) => (
                  <li key={b.booking_id}>
                    <Link to={`/booking/${b.booking_id}/status`} className="flex items-center gap-4 rounded-2xl border border-black/8 p-3.5 hover:border-brand transition-colors" data-testid="my-booking-item">
                      {b.cover_image && <img src={b.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-bold text-ink truncate">{b.property_name || b.room_name || "Your stay"}</p>
                        <p className="text-xs text-ink-soft truncate">{b.check_in && b.check_out ? `${b.check_in} → ${b.check_out}` : "Dates pending"}</p>
                        {b.status && <span className="mt-1 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-soft text-brand capitalize">{b.status}</span>}
                      </div>
                      {b.amount != null && <span className="shrink-0 font-heading font-bold text-ink">{fmtINR(b.amount)}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Contact details */}
        <div className="mt-6 rounded-3xl border border-black/8 bg-white p-6 md:p-8">
          <h2 className="font-heading font-bold text-lg text-ink">Contact details</h2>
          <p className="text-sm text-ink-soft mt-1">These autofill the guest form when you book.</p>
          <div className="mt-5 space-y-4">
            <Field label="Full name" name="guest_name" value={form.guest_name} onChange={onChange} placeholder="e.g. Priya Sharma" testid="profile-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone" name="guest_phone" value={form.guest_phone} onChange={onChange} placeholder="+91 98765 43210" testid="profile-phone" />
              <Field label="Email" name="guest_email" value={form.guest_email} onChange={onChange} type="email" placeholder="you@example.com" testid="profile-email" />
            </div>
          </div>
          <button onClick={onSave} data-testid="profile-save" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand text-white font-semibold px-6 h-12 transition-transform hover:-translate-y-0.5">
            <Check className="w-4 h-4" /> Save details
          </button>
        </div>
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Profile;
